"use client";

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function SafetyFeaturesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const safetyFeatures = [
    {
      id: 'safety_verification',
      icon: 'ShieldCheckIcon' as const,
      title: 'Verified Profiles',
      description: 'ID and photo verification required for all users. We use AI facial recognition to confirm identity and prevent catfishing.',
      color: 'primary',
    },
    {
      id: 'safety_video',
      icon: 'VideoCameraIcon' as const,
      title: 'Video Chat First',
      description: 'Built-in video dating lets you meet face-to-face before sharing personal contact info or meeting in person.',
      color: 'secondary',
    },
    {
      id: 'safety_moderation',
      icon: 'EyeIcon' as const,
      title: '24/7 Moderation',
      description: 'AI + human moderators monitor for inappropriate behavior. Report/block tools with immediate action on violations.',
      color: 'accent',
    },
    {
      id: 'safety_privacy',
      icon: 'LockClosedIcon' as const,
      title: 'Privacy Controls',
      description: 'Control who sees your profile. Hide from specific people, pause your account anytime, or go fully incognito mode.',
      color: 'success',
    },
  ];

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-muted/10 border-y border-white/5">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success/10 text-success text-xs uppercase tracking-widest font-semibold mb-6">
            <Icon name="ShieldCheckIcon" variant="solid" size={16} />
            Safety First
          </div>
          <h2
            className={`text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Date with <span className="text-primary">Confidence</span>
          </h2>
          <p
            className={`text-lg text-muted-foreground font-light transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Your safety is our top priority. Every feature is designed with security and privacy in mind.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {safetyFeatures.map((feature, index) => (
            <div
              key={feature.id}
              className={`glass-card-hover p-8 rounded-2xl transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${
                  feature.color === 'primary' ?'bg-primary/10'
                    : feature.color === 'secondary' ?'bg-secondary/10'
                    : feature.color === 'accent' ?'bg-accent/10' :'bg-success/10'
                }`}
              >
                <Icon
                  name={feature.icon}
                  variant="solid"
                  size={32}
                  className={
                    feature.color === 'primary' ?'text-primary'
                      : feature.color === 'secondary' ?'text-secondary'
                      : feature.color === 'accent' ?'text-accent' :'text-success'
                  }
                />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-4">{feature.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Trust Stats */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="text-4xl font-bold text-primary mb-2">100%</div>
            <div className="text-sm text-muted-foreground uppercase tracking-wider">Verified Users</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-secondary mb-2">&lt;5min</div>
            <div className="text-sm text-muted-foreground uppercase tracking-wider">Average Response Time</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-success mb-2">99.9%</div>
            <div className="text-sm text-muted-foreground uppercase tracking-wider">Incident-Free Dates</div>
          </div>
        </div>
      </div>
    </section>
  );
}